import { useCallback, useState } from 'react';
import { Linking, Platform } from 'react-native';
import { createURL } from 'expo-linking';
import { useAuth } from '../contexts/AuthContext';
import { trpc } from '../lib/trpc';
import { track } from '../lib/analytics';
import type { BillingPeriod } from '../lib/pricing-plans';
import { useMobileBillingMode, type MobileBillingMode } from '../lib/mobile-billing';

type PaidPlan = 'pro' | 'pro-family';

/** Structural view of a tRPC error's data envelope — enough to read the
 *  error code without importing the AppRouter error generics. */
type BillingErrorLike = { data?: { code?: string | null } | null | undefined };

interface UsePlanUpgradeOptions {
  /** Trigger/eyebrow forwarded to the billing.plan_changed event so the
   *  conversion can be attributed to the surface that prompted it. */
  trigger?: string | undefined;
  /** Invoked right before redirecting to Stripe Checkout for a NEW
   *  subscription (e.g. close a modal). The redirect navigates away, so
   *  this is the surface's only chance to clean up. */
  onCheckoutRedirect?: (() => void) | undefined;
  /** Invoked after a successful in-app plan change — the tier has
   *  already been refreshed into AuthContext. `tier` is the new tier. */
  onChanged?: ((tier: string) => void) | undefined;
  /** Invoked when the change was a no-op (already on the selected plan),
   *  so the surface can confirm gently without celebrating. */
  onNoop?: (() => void) | undefined;
}

interface UsePlanUpgrade {
  /** True when the user already has a live subscription, so a plan
   *  selection modifies it in place (changePlan) instead of opening a
   *  second Checkout. Uses the canonical `!== 'basic'` tier predicate. */
  hasPaidSub: boolean;
  /** Resolved mobile billing mode (see `useMobileBillingMode`):
   *  `'native'` runs the real in-app flow (always true on web),
   *  `'link_out'` sends the user to the web subscribe page instead of
   *  running Checkout/changePlan in-app, `'off'` is the server
   *  kill-switch — surfaces use this to hide their CTA. */
  mode: MobileBillingMode;
  /** Route a plan selection:
   *  - `mode === 'link_out'` → link out to the web subscribe page.
   *  - `mode === 'off'` → no-op (surfaces hide the CTA).
   *  - `mode === 'native'` → free user → Checkout (new subscription),
   *    existing subscriber → in-place changePlan (ADR-0088). */
  startUpgrade: (tier: PaidPlan, billingPeriod: BillingPeriod) => void;
  busy: boolean;
  error: string | null;
  clearError: () => void;
}

function mapBillingError(err: BillingErrorLike): string {
  const code = err.data?.code;
  if (code === 'CONFLICT') {
    // Server double-bill backstop: the cached tier said "free" but Stripe
    // shows a live subscription. Nudge the user to refresh rather than
    // leaving them on a generic error.
    return 'You already have an active subscription. Try refreshing the app.';
  }
  if (code === 'PRECONDITION_FAILED') {
    return "We can't process that right now. Please try again later.";
  }
  return 'Something went wrong. Please try again.';
}

/**
 * Shared plumbing for the two upgrade surfaces (the public /pricing page
 * and the in-product UpgradeSheet). Owns the free-vs-paid routing, the
 * Checkout / changePlan mutations, the post-change tier refresh, and
 * error mapping, so the two surfaces can't drift apart (ADR-0088). Each
 * surface keeps its own layout, its own "started"/click analytics, and
 * decides what to do after success via the callbacks.
 */
export function usePlanUpgrade(options: UsePlanUpgradeOptions = {}): UsePlanUpgrade {
  const { trigger, onCheckoutRedirect, onChanged, onNoop } = options;
  const { user, setUserInfo } = useAuth();
  const utils = trpc.useUtils();
  const [error, setError] = useState<string | null>(null);
  const { mode, upgradeUrl } = useMobileBillingMode();

  const hasPaidSub = !!user && user.subscriptionTier !== 'basic';

  const checkout = trpc.billing.createUpgradeCheckout.useMutation({
    onSuccess: ({ url }) => {
      onCheckoutRedirect?.();
      void Linking.openURL(url);
    },
    onError: (err) => setError(mapBillingError(err)),
  });

  const changePlan = trpc.billing.changePlan.useMutation({
    onSuccess: async ({ changed, tier }, variables) => {
      // A no-op (already on the selected plan) returns changed:false —
      // don't fire a conversion event or refresh the tier for a change
      // that didn't happen.
      if (!changed) {
        onNoop?.();
        return;
      }
      track('billing.plan_changed', {
        tier,
        billingPeriod: variables.billingPeriod,
        trigger: trigger ?? null,
      });
      // The server already wrote the new tier synchronously, but the
      // cached AuthContext.user / JWT still carries the old one — refresh
      // so entitlements reflect immediately.
      const me = await utils.auth.me.fetch();
      setUserInfo(me);
      onChanged?.(tier);
    },
    onError: (err) => setError(mapBillingError(err)),
  });

  // React Query memoizes `.mutate`, so these are stable across renders
  // even though the enclosing mutation objects are not.
  const checkoutMutate = checkout.mutate;
  const changePlanMutate = changePlan.mutate;

  const startUpgrade = useCallback(
    (tier: PaidPlan, billingPeriod: BillingPeriod) => {
      setError(null);

      if (mode === 'off') {
        // Server kill-switch — surfaces already hide the CTA; this is
        // just a defensive no-op guard.
        return;
      }

      if (mode === 'link_out') {
        // Apple/Google forbid selling the digital subscription via an
        // in-app Stripe checkout on native — link out to the web
        // subscribe page instead. No checkout/changePlan mutation, no
        // extra analytics here: the calling surface owns its own
        // funnel events (mirrors how the in-app checkout's onSuccess
        // doesn't double-track either).
        onCheckoutRedirect?.();
        void Linking.openURL(upgradeUrl);
        return;
      }

      // mode === 'native' — unchanged existing behavior (web, or a
      // native build with no kill-switch override, though in practice
      // native always resolves to 'link_out' or 'off').
      if (hasPaidSub) {
        changePlanMutate({ tier, billingPeriod });
        return;
      }
      // trialDays is omitted — the server defaults to its 7-day window
      // and still skips the trial for anyone with prior history.
      checkoutMutate({
        tier,
        billingPeriod,
        successUrl: createURL('/upgrade/success'),
        cancelUrl: createURL('/upgrade/cancel'),
        // Server refuses native-originated checkouts (external-purchase
        // compliance). Only reached on web here — native branches to
        // link_out/off above — but sent explicitly so the server can enforce.
        platform: Platform.OS === 'web' ? 'web' : Platform.OS === 'ios' ? 'ios' : 'android',
      });
    },
    // Depend on the (stable) mutate fns rather than the mutation objects,
    // which churn on every isPending flip. startUpgrade then only changes
    // identity when hasPaidSub/mode/upgradeUrl do — which is exactly when
    // routing should.
    [mode, upgradeUrl, hasPaidSub, changePlanMutate, checkoutMutate, onCheckoutRedirect]
  );

  return {
    hasPaidSub,
    mode,
    startUpgrade,
    busy: checkout.isPending || changePlan.isPending,
    error,
    clearError: useCallback(() => setError(null), []),
  };
}
